import { defineStore } from 'pinia';
import { strapi } from '~/stores/transport/strapi';
import {
  EHistoryFilters,
  EBonusStatus,
  ETransactionAction,
  ETransactionStatus,
  type ISsTransactionPayments,
} from '~/types/softswiss/ssTransactionHistory';
import { assignDataToFields } from '~/utils/strapiComponents/addStrapiData';
import { useStoreCurrencyMenu } from '../currencyMenu';
import type { IFilter, IFilterListItem } from '~/types/historyFilter';

export const useStoreModalTransactionHistory = defineStore('modalTransactionHistory', {
  state: () => ({
    locale: '',
    title: '',

    transactionHistoryTitle: '',
    transactionHistoryNoticeText: '',
    seeAllTransactionTitle: '',

    dateTitle: '',
    statusTitle: '',
    actionTitle: '',
    paymentSystemTitle: '',
    amountTitle: '',

    betsHistoryTitle: '',
    betsHistoryNoticeText: '',
    seeAllBetsHistoryTitle: '',
    winTitle: '',

    bonusHistoryTitle: '',
    bonusHistoryNoticeText: '',
    seeAllBonusHistoryTitle: '',
    stageTitle: '',
    wagerTitle: '',
    validUntilTitle: '',
    statusAcceptedTitle: '',
    statusDiscardeTitle: '',
    statusPendingTitle: '',

    filterCurrencyTitle: '',
    filterActionsTitle: '',
    filterStatusTitle: '',
    filterActionsDepositTitle: '',
    filterActionsCashoutTitle: '',
    filterActionsAffiliateRewardTitle: '',
    filterStatusWagerDoneTitle: '',
    filterStatusLostTitle: '',
    filterStatusActiveTitle: '',
    filterStatusCanceledTitle: '',
    filterStatusExpiredTitle: '',
    filterDateDayTitle: '',
    noFilteredResultsTitle: '',
    allCurrenciesTitle: '',
    allStatusesTitle: '',
    allActionsTitle: '',
    requestPromise: null as Promise<any> | null,
  }),

  getters: {
    currencyFilterOptions(): IFilterListItem[] {
      return useStoreCurrencyMenu().activeCurrencies.map((currency) => ({
        value: currency.currencyCode,
        title: currency.currencyCode,
        iconUrl: currency.currencyIcon.url,
      }));
    },
    transactionStatusFilterOptions(): IFilterListItem[] {
      return [
        { value: ETransactionStatus.Pending, title: this.statusPendingTitle },
        { value: ETransactionStatus.Discarded, title: this.statusDiscardeTitle },
        { value: ETransactionStatus.Accepted, title: this.statusAcceptedTitle },
      ];
    },
    transactionActionFilterOptions(): IFilterListItem[] {
      return [
        { value: ETransactionAction.Deposit, title: this.filterActionsDepositTitle },
        { value: ETransactionAction.Cashout, title: this.filterActionsCashoutTitle },
        { value: ETransactionAction.Reward, title: this.filterActionsAffiliateRewardTitle },
      ];
    },
    bonusStatusFilterOptions(): IFilterListItem[] {
      return [
        { value: EBonusStatus.WagerDone, title: this.filterStatusWagerDoneTitle },
        { value: EBonusStatus.Lost, title: this.filterStatusLostTitle },
        { value: EBonusStatus.HandleBets, title: this.filterStatusActiveTitle },
        { value: EBonusStatus.Canceled, title: this.filterStatusCanceledTitle },
        { value: EBonusStatus.Expired, title: this.filterStatusExpiredTitle },
      ];
    },
    transactionFilters(): IFilter[] {
      return [
        {
          title: this.filterCurrencyTitle,
          id: EHistoryFilters.Currency,
          list: this.currencyFilterOptions,
          allCheckedTitle: this.allCurrenciesTitle,
        },
        {
          title: this.filterStatusTitle,
          id: EHistoryFilters.Status,
          list: this.transactionStatusFilterOptions,
          allCheckedTitle: this.allStatusesTitle,
        },
        {
          title: this.filterActionsTitle,
          id: EHistoryFilters.Action,
          list: this.transactionActionFilterOptions,
          allCheckedTitle: this.allActionsTitle,
        },
      ];
    },
    bonusFilters(): IFilter[] {
      return [
        {
          title: this.filterCurrencyTitle,
          id: EHistoryFilters.Currency,
          list: this.currencyFilterOptions,
          allCheckedTitle: this.allCurrenciesTitle,
        },
        {
          title: this.filterStatusTitle,
          id: EHistoryFilters.Status,
          list: this.bonusStatusFilterOptions,
          allCheckedTitle: this.allStatusesTitle,
        },
      ];
    },
    betsFilters(): IFilter[] {
      return [
        {
          title: this.filterCurrencyTitle,
          id: EHistoryFilters.Currency,
          list: this.currencyFilterOptions,
          allCheckedTitle: this.allCurrenciesTitle,
        },
      ];
    },
  },

  actions: {
    async loadHistoryModal() {
      if (!this.requestPromise) {
        this.requestPromise = strapi()
          .get('transaction-history-modal')
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        assignDataToFields(this, response.data);

        return true;
      } else {
        return false;
      }
    },
    getTransactionStatus(transaction: ISsTransactionPayments) {
      if (transaction.success) {
        return ETransactionStatus.Accepted;
      } else if (transaction.finished_at) {
        return ETransactionStatus.Discarded;
      } else {
        return ETransactionStatus.Pending;
      }
    },
  },
});
